import React, { useContext } from "react";

import { useMemo, useState, useEffect, useCallback, createContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import { ISalesChannel } from "../../services/ecommerceApi/SalesChannel/types";
import { getOrder, addToCart } from "../../store/order";
import { OrderType } from "../../types";
import { graphql, useStaticQuery } from "gatsby";

type IEcommerceContext = {
    flyInOpen: boolean;
    setFlyInOpen: (open: boolean) => void;
    postcode: string | undefined;
    setPostcode: (postcode: string) => void;
    editPostcode: boolean;
    setEditPostcode: (edit: boolean) => void;
    setSalesChannel: (salesChannel: ISalesChannel) => void;
    addItemToCart: (skuId: string, quantity: number) => Promise<OrderType | undefined>;
    loqateConfig?: {
        property: string;
        propertyId: string;
        value: string;
    }[];
}

// ============== CONTEXT ==============
export const EcommerceContext = createContext({} as IEcommerceContext);

// ============== PROVIDER ==============
type Props = {
    children: React.ReactNode;
};

export function EcommerceProvider({ children }: Props) {

    const { loqate } = useStaticQuery<LoqateQueryType>(LOQATE_QUERY);
    const loqateConfig = loqate?.nodes?.[0]?.config;

    const myAddresses = useSelector((state: RootState) => state.order.addresses);
    const orderAddresses = useSelector((state: RootState) => state.order.order?.shippingAddress);
    const orderId = useSelector((state: RootState) => state.order.order?.id);
    const dispatch = useDispatch<AppDispatch>();

    const [flyInOpen, setFlyInOpen] = useState<boolean>(false);
    const [salesChannel, setSalesChannel] = useState<ISalesChannel | undefined>(undefined);
    const [internalPostcode, setInternalPostcode] = useState<string | undefined>(undefined);
    const [editPostcode, setEditPostcode] = useState(false);

    const refreshOrder = useCallback(async () => {
        if (!orderId || !dispatch || !getOrder) {
            return;
        }
        await dispatch(getOrder(orderId));
    }, [orderId, dispatch, getOrder]);

    useEffect(() => {
        refreshOrder();
    }, [refreshOrder]);

    const postcode = useMemo(() => {

        if (internalPostcode) {
            return internalPostcode;
        }

        if (orderAddresses?.length) {
            return orderAddresses[0].postCode;
        }

        if (myAddresses.length) {
            return myAddresses[0].postCode;
        }

        return salesChannel?.defaultPostcode;
    }, [salesChannel?.defaultPostcode, internalPostcode, myAddresses]);

    const initialize = useCallback(async () => {
        const lsPostcode = localStorage.getItem("ecomHelper:postcode");
        if (lsPostcode) {
            setInternalPostcode(lsPostcode);
        }
    }, []);

    const setPostcode = useCallback((postcode: string) => {
        setInternalPostcode(postcode);
    }, [postcode]);

    const addItemToCart = useCallback(async (skuId: string, quantity: number) => {
        const { payload } = await dispatch(addToCart({
            orderId,
            skuId,
            quantity
        })) as { payload: OrderType | undefined };
        const success = payload && payload.id && (!payload.errors || payload.errors.length === 0);
        if (success) {
            setFlyInOpen(true);
        }
        return payload;
    }, [orderId, dispatch, addToCart]);

    useEffect(() => {
        initialize();
    }, [initialize]);

    useEffect(() => {
        if (internalPostcode) {
            localStorage.setItem("ecomHelper:postcode", internalPostcode);
        }
    }, [internalPostcode]);

    const memoizedValue = useMemo(
        () =>
        ({
            flyInOpen,
            setFlyInOpen,
            postcode,
            setPostcode,
            editPostcode,
            setEditPostcode,
            setSalesChannel,
            addItemToCart,
            loqateConfig
        } as IEcommerceContext),
        [postcode, setPostcode, internalPostcode, editPostcode, setEditPostcode, setSalesChannel, flyInOpen, setFlyInOpen, addItemToCart, loqateConfig]
    );

    return (
        <EcommerceContext.Provider value={memoizedValue}>
            {children}
        </EcommerceContext.Provider>
    );
}

export type LoqateConfig = {
    property: string;
    propertyId: string;
    value: string;
}[];
type LoqateQueryType = {
    loqate: {
        nodes: {
            id: string;
            config: LoqateConfig
        }[];
    };
}
const LOQATE_QUERY = graphql`
    query {
        loqate: allIntegration(filter: { type: { eq: "loqate" } }) {
            nodes {
                id
                config {
                    property
                    propertyId
                    value
                }
            }
        }
    }
`;

// ============== HOOK ==============
export const useEcommerceHelper = () => {
    const context = useContext(EcommerceContext);
    if (!context) { throw new Error("useEcommerceHelper context must be use inside EcommerceProvider"); }
    return context;
};